import { useEffect, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import Skeleton from "react-loading-skeleton";

import { Stack } from "capitalrx-components-2.0/lib/layout";
import { useAlert, Link } from "capitalrx-components-2.0/lib/general";
import { queryString, isValidLatitude, isValidLongitude, formatZip } from "capitalrx-utilities";

import { DEFAULT_MAP_COORDINATES } from "@/common/constants/mapConstants";
import { useGetPharmacy } from "@/api-hooks";
import { PharmacyJourneyLayout } from "@/basic/layouts/PharmacyJourneyLayout";
import { PharmacyDetailsContainer, MapCoordinates, PharmacyDetailsType, Heading, Body, getAppleMapsURL } from "@/basic/components";

import { FormularyParams } from "@/basic/components/FormularyButtonContainer";

import styles from "./styles.module.scss";

export const PharmacyJourneyDetails = () => {
  const { t: translate } = useTranslation();
  const getCurrentQueryStrings = queryString.convertQueryStringToJson(location.search);

  const { formulary }: FormularyParams = useParams();

  const [coordinates, setCoordinates] = useState<MapCoordinates>({ latitude: null, longitude: null });
  const [pharmacyData, setPharmacyData] = useState<Array<PharmacyDetailsType>>();

  const { data: pharmacy, isLoading, isError } = useGetPharmacy(
    {
      provider_id: getCurrentQueryStrings?.provider_id?.toString(),
    },
    {
      enabled: !!getCurrentQueryStrings?.provider_id,
    },
  );

  const showAlert = useAlert();

  useEffect(() => {
    if (!pharmacy) return;

    setPharmacyData([{ pharmacy }]);

    setCoordinates({
      latitude: Number(getCurrentQueryStrings.lat),
      longitude: Number(getCurrentQueryStrings.lng),
    });
  }, [location, pharmacy]);

  const failureHandler = useCallback(() => {
    showAlert({ type: "error", style: "toast", message: translate("PHARMACY_JOURNEY_DETAILS.anErrorOcurred") });
  }, [showAlert]);

  if (isError) {
    failureHandler();
  }

  const center = {
    lat: DEFAULT_MAP_COORDINATES.lat,
    lng: DEFAULT_MAP_COORDINATES.lng,
  };

  if (isValidLatitude(coordinates.latitude) && isValidLongitude(coordinates.longitude)) {
    center.lat = coordinates.latitude;
    center.lng = coordinates.longitude;
  }

  const backButtonParams = queryString.convertJsonToQueryString({
    zip_code: getCurrentQueryStrings?.zip_code?.toString(),
    lat: getCurrentQueryStrings?.lat?.toString(),
    lng: getCurrentQueryStrings?.lng?.toString(),
  });

  const address = `${pharmacy?.address_line_1}, ${pharmacy?.address_line_2 && `${pharmacy?.address_line_2} , `}${pharmacy?.city},
  ${pharmacy?.state}, ${formatZip(pharmacy?.zip)}`;
  const url = getAppleMapsURL({ data: { address } });

  const topSectionContent = (
    <Stack spacing="mini" className={styles["topSectionContent"]}>
      <Heading>{isLoading ? <Skeleton /> : pharmacy?.name}</Heading>
      <Body>
        {isLoading ? (
          <Skeleton />
        ) : (
          <Link to={url}>
            {address}
          </Link>
        )}
      </Body>
    </Stack>
  );

  return (
    <PharmacyJourneyLayout
      backButtonParams={backButtonParams}
      backButtonUrl={`/${formulary}/pharmacy/list`}
      topSectionContent={topSectionContent}
      detailsPage
    >
      <PharmacyDetailsContainer pharmacyData={pharmacyData} data={pharmacy} isLoading={isLoading} center={center} />
    </PharmacyJourneyLayout>
  );
};
